import React, { useEffect, useRef, useState } from "react";
import { Button, Card, Row, Col, Form } from "react-bootstrap";
import SimpleBar from "simplebar-react";
import avatar2 from "../assets/images/ai-logo.webp";
import axios from "axios";
import apiUrl, { localPath } from "slices/apiEndpoint";
import { getDecodeId, getUserName } from "slices/userProfile/thunk";
import api from "./api";

const AIContractChatModal = ({ show, handleClose,  }: any) => {
  const userId = getDecodeId()
  const [messages, setMessages] = useState<any[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const userName = getUserName();

  const [homeownerSearch, setHomeownerSearch] = useState("");
  const [homeownerResults, setHomeownerResults] = useState<any[]>([]);
  const [waitingForHomeowner, setWaitingForHomeowner] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (show) {
      setMessages([
        {
          id: "welcome",
          sender: "ai",
          body: "👋 Hey! I'm here to help you create your contract. Just tell me your requirement ",
          time: new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
        },
      ]);
    }
  }, [show]);

  useEffect(() => {
    if (messages.length > 4) scrollToBottom();
  }, [messages]);

  const handleHomeownerSearch = async () => {
    try {
      const res = await api.post(`/proposal/searchHomeowner`, {
        email: homeownerSearch,
      });
      setHomeownerResults(res.data || []);
    } catch (err) {
      console.error("Homeowner search failed:", err);
      setHomeownerResults([]);
    }
  };

  const handleHomeownerSelect = async (homeowner: any) => {
    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      body: `Selected homeowner: ${homeowner.user_first_name} (${homeowner.user_email})`,
      time: new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
    setMessages((prev) => [...prev, userMessage]);
    setHomeownerSearch("");
    setHomeownerResults([]);
    setWaitingForHomeowner(false);

    // Send selected ID to backend
    try {
      const response = await api.post(`/api/contract/chat`, {
        userId,
        userName,
        message: `Selected homeowner ID: ${homeowner.id}`,
      });

      const aiReply = {
        id: `ai-${Date.now()}`,
        sender: "ai",
        body: response.data.reply,
        time: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      };
      setMessages((prev) => [...prev, aiReply]);
    } catch (err) {
      console.error(err);
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim()) return;

    const userMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      body: messageInput,
      time: new Date().toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessageInput("");
    scrollToBottom();
    setLoading(true);

    try {
      const response = await api.post(`/api/contract/chat`, {
        userId,
        userName,
        message: messageInput,
      });

      const aiReply = {
        id: `ai-${Date.now()}`,
        sender: "ai",
        body: response.data.reply,
        time: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      };

      setMessages((prev) => [...prev, aiReply]);
      if (response.data.reply.toLowerCase().includes("homeowner")) {
        setWaitingForHomeowner(true);
      }
    } catch (error) {
      const errorReply = {
        id: `ai-${Date.now()}`,
        sender: "ai",
        body: "Oops! Something went wrong. Please try again later.",
        time: new Date().toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      };
      setMessages((prev) => [...prev, errorReply]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`email-chat-detail ${show ? "d-block" : "d-none"}`} id="ai-contract-modal">
      <Card className="mb-0 overflow-hidden">
        <Card.Header className="align-items-center d-flex bg-primary-subtle text-primary p-3">
          <div className="flex-grow-1">
            <h5 className="fs-md text-primary-emphasis profile-username">Contract Assistant 🤖</h5>
            <p className="mb-0 fs-sm lh-1">tract Bot</p>
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              className="btn btn-link btn-icon btn-sm text-primary fs-lg text-decoration-none rounded-circle bg-primary-subtle bg-opacity-10"
              onClick={handleClose}
            >
              <i className="ri-close-fill"></i>
            </button>
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <div id="ai-chat tailwind-prefix">
            <SimpleBar style={{ height: "250px" }}>
              <div className="chat-conversation p-3">
                <ul className="list-unstyled chat-conversation-list chat-sm">
                  {messages.map((msg) => (
                    <li key={msg.id} className={`chat-list ${msg.sender === "user" ? "right" : "left"}`}>
                      <div className="conversation-list">
                        {msg.sender === "ai" && (
                          <div className="chat-avatar flex items-center">
                            <img src={avatar2} alt="AI Avatar" style={{
                              height:30,
                              width:30
                            }}/>
                          </div>
                        )}
                        <div className="user-chat-content">
                          <div className="ctext-wrap">
                            <div className="ctext-wrap-content">
                              <p className="mb-0 ctext-content">{msg.body}</p>
                            </div>
                          </div>
                          <div className="conversation-name">
                            <small className="text-muted time">{msg.time}</small>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                  <div ref={messagesEndRef} />
                </ul>
              </div>
            </SimpleBar>
          </div>

          {waitingForHomeowner && (
            <div className="px-3 pb-3">
              <Form.Control
                type="text"
                placeholder="Enter homeowner email or name"
                value={homeownerSearch}
                onChange={(e) => setHomeownerSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleHomeownerSearch()}
                className="mb-2"
              />
              <Button variant="outline-secondary" size="sm" onClick={handleHomeownerSearch}>
                Search
              </Button>
              <ul className="mt-2 list-unstyled">
                {homeownerResults.map((h) => (
                  <li
                    key={h.id}
                    className="p-2 bg-light mb-1 rounded"
                    style={{ cursor: "pointer" }}
                    onClick={() => handleHomeownerSelect(h)}
                  >
                    {h.user_first_name} - {h.user_email}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="border-top border-top-dashed">
            <Row className="g-2 mx-3 mt-2 mb-3">
              <Col className="col">
                <Form.Control
                  type="text"
                  className="border-light bg-light"
                  placeholder="Enter Message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                />
              </Col>
              <Col className="col-auto">
                <Button onClick={sendMessage} variant="secondary" disabled={loading}>
                  {loading ? <i className="mdi mdi-loading mdi-spin"></i> : <i className="mdi mdi-send"></i>}
                </Button>
              </Col>
            </Row>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AIContractChatModal;